<template>
    <div v-if="autorizado">
    <div class="titulo">
        <div class="margem container">
            <div class="m-icone direita">
                <button class="acao-secundaria" @click="abrirModalNovo">Nova Despesa</button>
            </div>
            <div class="m-icone direita">
                <div class="pesquisa">
                    <input v-model="busca" type="text" placeholder="Buscar fornecedor, projeto, motivo…"
                        @keyup.enter="carregar" @input="onBuscaInput">
                    <a class="icone-pesquisa" @click.prevent="carregar"></a>
                </div>
            </div>
            <h2>Lançamento de Despesa de Projeto</h2>
        </div>
    </div>

    <div class="margem container">
        <div class="bloco margem">
            <div v-if="loading" class="loading">
                <div></div>
            </div>

            <table v-else class="tabela">
                <tbody>
                    <tr>
                        <th>Código</th>
                        <th>Projeto</th>
                        <th>Data</th>
                        <th>Fornecedor</th>
                        <th>Motivo</th>
                        <th>Valor</th>
                        <th>Responsável</th>
                        <th></th>
                    </tr>
                    <tr v-if="despesas.length === 0">
                        <td colspan="8" class="alinha-centro fonte-fraca">Nenhuma despesa lançada.</td>
                    </tr>
                    <tr v-for="item in despesas" :key="item.id" class="linha-despesa" @click="abrirVisualizacao(item)">
                        <td>{{ item.projeto?.codigo || '-' }}</td>
                        <td>{{ item.projeto?.nome || '-' }}</td>
                        <td>{{ formatarData(item.data) }}</td>
                        <td>{{ item.fornecedor || '-' }}</td>
                        <td>{{ item.motivacao?.nome || '-' }}</td>
                        <td>{{ formatarValor(item.valor) }}</td>
                        <td>{{ item.responsavel?.name || '-' }}</td>
                        <td @click.stop>
                            <v-menu>
                                <template v-slot:activator="{ props }">
                                    <v-btn
                                        style="width: 2rem; height: 2rem; border: 1px solid var(--cor-separador); color: var(--cor-fonte);"
                                        class="acao-secundaria" icon="mdi-dots-horizontal" v-bind="props">
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item @click="editar(item)">Editar</v-list-item>
                                    <v-list-item style="color: red;" @click="excluir(item)">Excluir</v-list-item>
                                </v-list>
                            </v-menu>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <ModalDespesaProjeto ref="modalDespesa" :mostrar="mostrarModal" :projetos="projetos" :motivacoes="motivacoes"
        :categorias="categorias" :usuarios="usuarios" :despesa-para-editar="despesaParaEditar"
        @fechar="fecharModal" @salvar="onSalvar" />

    <ModalVisualizarDespesaProjeto :mostrar="mostrarVisualizacao" :despesa="despesaVisualizacao"
        @fechar="fecharVisualizacao" />
    </div>
</template>

<script>
import ModalDespesaProjeto from '@/components/Modais/projetos/ModalDespesaProjeto.vue'
import ModalVisualizarDespesaProjeto from '@/components/Modais/projetos/ModalVisualizarDespesaProjeto.vue'
import { useApiError } from '@/composables/useApiError'
import { getPermissao } from '@/services/permissao-service'
import {
    atualizarDespesaProjeto,
    excluirDespesaProjeto,
    gravarDespesaProjeto,
    listarCategoriasDespesa,
    listarDespesasProjeto,
    listarMotivacoesDespesa,
    listarProjetosDespesa,
} from '@/services/serviceDespesaProjeto'
import { consultarSetores } from '@/services/usuario-setor'

const FUNC_AUDITOR = 116
const FUNC_LANCAR_DESPESA = 269

export default {
    name: 'LancamentoDespesaProjeto',

    components: { ModalDespesaProjeto, ModalVisualizarDespesaProjeto },

    setup() {
        const { handleError } = useApiError()
        return { handleApiError: handleError }
    },

    data() {
        return {
            loading: true,
            autorizado: false,
            despesas: [],
            projetos: [],
            motivacoes: [],
            categorias: [],
            usuarios: [],
            busca: '',
            mostrarModal: false,
            despesaParaEditar: null,
            buscaTimeout: null,
            mostrarVisualizacao: false,
            despesaVisualizacao: null,
        }
    },

    async mounted() {
        const funcionalidades = await getPermissao()
        this.autorizado = Array.isArray(funcionalidades)
            && (funcionalidades.includes(FUNC_AUDITOR) || funcionalidades.includes(FUNC_LANCAR_DESPESA))

        if (!this.autorizado) {
            this.loading = false
            this.$router.replace('/')
            return
        }

        await Promise.all([this.carregarAuxiliares(), this.carregar()])
    },

    methods: {
        formatarValor(valor) {
            if (valor == null || Number.isNaN(Number(valor))) return '-'
            return Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        },
        formatarData(data) {
            if (!data) return '-'
            const [ano, mes, dia] = String(data).slice(0, 10).split('-')
            if (!ano || !mes || !dia) return data
            return `${dia}/${mes}/${ano}`
        },
        onBuscaInput() {
            clearTimeout(this.buscaTimeout)
            this.buscaTimeout = setTimeout(() => {
                this.carregar()
            }, 350)
        },
        async carregarAuxiliares() {
            try {
                const [projetos, motivacoes, categorias, setoresUsuarios] = await Promise.all([
                    listarProjetosDespesa(),
                    listarMotivacoesDespesa(),
                    listarCategoriasDespesa(),
                    consultarSetores(),
                ])
                this.projetos = projetos
                this.motivacoes = motivacoes
                this.categorias = categorias
                this.usuarios = setoresUsuarios?.usuarios || []
            } catch (error) {
                this.handleApiError(error)
            }
        },
        async carregar() {
            this.loading = true
            try {
                this.despesas = await listarDespesasProjeto({
                    busca: this.busca || undefined,
                })
            } catch (error) {
                this.handleApiError(error)
                this.despesas = []
            } finally {
                this.loading = false
            }
        },
        abrirModalNovo() {
            this.despesaParaEditar = null
            this.mostrarModal = true
        },
        editar(item) {
            this.despesaParaEditar = { ...item }
            this.mostrarModal = true
        },
        abrirVisualizacao(item) {
            this.despesaVisualizacao = { ...item }
            this.mostrarVisualizacao = true
        },
        fecharVisualizacao() {
            this.mostrarVisualizacao = false
            this.despesaVisualizacao = null
        },
        fecharModal() {
            this.mostrarModal = false
            this.despesaParaEditar = null
            this.$refs.modalDespesa?.resetSalvando?.()
        },
        async onSalvar(payload) {
            try {
                if (payload.id) {
                    const { id, ...dados } = payload
                    await atualizarDespesaProjeto(id, dados)
                } else {
                    await gravarDespesaProjeto(payload)
                }
                this.fecharModal()
                await this.carregar()
            } catch (error) {
                this.$refs.modalDespesa?.resetSalvando?.()
                this.handleApiError(error)
            }
        },
        async excluir(item) {
            if (!confirm(`Excluir a despesa de ${item.fornecedor || 'fornecedor'} (${this.formatarValor(item.valor)})?`)) {
                return
            }
            try {
                await excluirDespesaProjeto(item.id)
                await this.carregar()
            } catch (error) {
                this.handleApiError(error)
            }
        },
    },
}
</script>

<style scoped>
.linha-despesa {
    cursor: pointer;
}
</style>
